import {
  AlertCircle,
  AlertTriangle,
  Briefcase,
  CalendarDays,
  CheckCircle2,
  HandCoins,
  HelpCircle,
  MapPin,
  MessageCircle,
  Package,
  Receipt,
  Wallet,
  XCircle,
} from 'lucide-react'
import type { LucideIcon, LucideProps } from 'lucide-react'
import { createContext, createElement, useContext, useMemo } from 'react'

export const ICON_REGISTRY = {
  'alert-circle': AlertCircle,
  'alert-triangle': AlertTriangle,
  briefcase: Briefcase,
  calendar: CalendarDays,
  'check-circle': CheckCircle2,
  'hand-coins': HandCoins,
  'help-circle': HelpCircle,
  'map-pin': MapPin,
  'message-circle': MessageCircle,
  package: Package,
  receipt: Receipt,
  wallet: Wallet,
  'x-circle': XCircle,
} satisfies Record<string, LucideIcon>

export type IconKey = keyof typeof ICON_REGISTRY

export type IconOverrideMap = Partial<Record<IconKey, LucideIcon>>

const ICON_KEY_ALIASES: Record<string, IconKey> = {
  alertcircle: 'alert-circle',
  'alert-circle': 'alert-circle',
  alerttriangle: 'alert-triangle',
  'alert-triangle': 'alert-triangle',
  briefcase: 'briefcase',
  calendar: 'calendar',
  calendardays: 'calendar',
  'calendar-days': 'calendar',
  checkcircle: 'check-circle',
  'check-circle': 'check-circle',
  'check-circle-2': 'check-circle',
  handcoins: 'hand-coins',
  'hand-coins': 'hand-coins',
  helpcircle: 'help-circle',
  'help-circle': 'help-circle',
  mappin: 'map-pin',
  'map-pin': 'map-pin',
  messagecircle: 'message-circle',
  'message-circle': 'message-circle',
  package: 'package',
  receipt: 'receipt',
  wallet: 'wallet',
  xcircle: 'x-circle',
  'x-circle': 'x-circle',
}

export function normalizeIconKey(iconKey?: string | null): IconKey | null {
  if (!iconKey) return null

  const normalized = iconKey.trim().toLowerCase().replace(/[_\s]+/g, '-')
  const compact = normalized.replace(/-/g, '')

  if (normalized in ICON_KEY_ALIASES) {
    return ICON_KEY_ALIASES[normalized]
  }

  if (compact in ICON_KEY_ALIASES) {
    return ICON_KEY_ALIASES[compact]
  }

  if (normalized in ICON_REGISTRY) {
    return normalized as IconKey
  }

  return null
}

export const IconOverridesContext = createContext<IconOverrideMap | undefined>(undefined)

export function resolveIconComponent(iconKey?: string | null, overrides?: IconOverrideMap): LucideIcon {
  const normalizedKey = normalizeIconKey(iconKey)

  if (normalizedKey && overrides?.[normalizedKey]) {
    return overrides[normalizedKey]!
  }

  if (normalizedKey) {
    return ICON_REGISTRY[normalizedKey]
  }

  return ICON_REGISTRY.briefcase
}

export function useResolvedIcon(iconKey?: string | null, overrides?: IconOverrideMap) {
  const contextOverrides = useContext(IconOverridesContext)
  return useMemo(
    () => resolveIconComponent(iconKey, overrides ?? contextOverrides),
    [iconKey, overrides, contextOverrides],
  )
}

export type ResolvedIconProps = LucideProps & {
  iconKey?: string | null
  overrides?: IconOverrideMap
}

/** Renders a registry icon without assigning a dynamic component during parent render. */
export function ResolvedIcon({
  iconKey,
  overrides,
  className,
  ...rest
}: ResolvedIconProps) {
  const contextOverrides = useContext(IconOverridesContext)
  const Icon = resolveIconComponent(iconKey, overrides ?? contextOverrides)
  return createElement(Icon, { className, ...rest })
}
